// Shared visual language for `flush` pages (the flush redesign):
// content lives in floating white rounded cards on a soft gray page
// background. Centralised here so a stylistic change (radius, shadow, fill)
// propagates to every section that opts in, instead of drifting per-page.

export const hairline = (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)');

export const softFill = (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F2F2F3');

// The floating content/list card itself.
export const surfaceCardSx = {
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: (theme) => theme.palette.background.paper,
  borderRadius: '16px',
  border: (theme) => `1px solid ${hairline(theme)}`,
  boxShadow: (theme) =>
    theme.palette.mode === 'dark'
      ? '0 1px 2px rgba(0,0,0,0.4)'
      : '0 1px 2px rgba(16,24,40,0.04), 0 6px 20px rgba(16,24,40,0.05)',
  overflow: 'hidden',
};

// Popovers/menus/dialogs float above the cards, so they keep their own
// (slightly heavier) border + shadow rather than the card treatment.
export const floatSurfaceSx = {
  borderRadius: '12px',
  border: (theme) => `1px solid ${hairline(theme)}`,
  boxShadow: (theme) => (theme.palette.mode === 'dark' ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.10)'),
};

// Standard header row for a surface card: title (+ optional subtitle) on the
// left, actions on the right, a hairline rule underneath.
export const cardHeaderSx = {
  px: { xs: '16px', md: '40px' },
  py: { xs: '14px', md: '24px' },
  borderBottom: (theme) => `1px solid ${hairline(theme)}`,
  flexShrink: 0,
};

// Standard scrollable body of a surface card.
export const cardBodySx = {
  flex: 1,
  minHeight: 0,
  px: { xs: '16px', md: '40px' },
  py: { xs: '20px', md: '32px' },
};

// A row in a secondary list rail (the promo list, the driver-status list).
// Quiet by default; the current item reads as a soft filled chip the way
// a selected sub-nav item does. Centralised so every rail shares
// one hover/selected treatment instead of re-deriving it per page.
export const railItemSx = {
  fontSize: '14px',
  py: '9px',
  px: '12px',
  mb: '1px',
  borderRadius: '10px',
  transition: 'background-color 0.12s ease',
  '&.Mui-selected': {
    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.09)' : '#EEF0F2'),
    fontWeight: 600,
    '&:hover': {
      backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.11)' : '#E8EAED'),
    },
  },
  '&:hover': {
    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F6F7F8'),
  },
};

// Quiet row-hover fill shared by list rows and table rows.
export const rowHover = (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#F6F7F8');

// The shared "filter pill" trigger: a rounded chip with a
// hairline border and quiet hover. Used by FilterSelect and by any toolbar
// dropdown trigger (Trips "Filters"/"Columns"), so every filter affordance
// across admin reads the same. Compose over it for extra state (e.g. an
// active/selected pill sets its own fill + border).
export const filterPillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  height: '34px',
  px: '12px',
  minWidth: 0,
  border: (theme) => `1px solid ${hairline(theme)}`,
  borderRadius: '10px',
  backgroundColor: 'transparent',
  color: 'text.primary',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '13px',
  fontWeight: 500,
  textTransform: 'none',
  transition: 'background-color 0.12s ease, border-color 0.12s ease',
  '&:hover': { backgroundColor: softFill, borderColor: (theme) => hairline(theme) },
};

// Primary action button in the Apple-style language: a solid filled pill
// (dark in light mode, white in dark mode) sharing the filter pills' radius +
// height. Pair with `filterPillSx` for secondary/outline actions so a toolbar's
// buttons and filters all read as one family.
export const pillButtonSx = {
  height: '34px',
  px: '16px',
  borderRadius: '10px',
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: 1,
  boxShadow: 'none',
  color: (theme) => theme.palette.background.paper,
  backgroundColor: (theme) => theme.palette.text.primary,
  '&:hover': { backgroundColor: (theme) => theme.palette.text.primary, opacity: 0.9, boxShadow: 'none' },
  '&.Mui-disabled': {
    backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'),
    color: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)'),
  },
};

// The "open search" bar (rail search icon → full-width bar over the content):
// a clean white rounded field with a hairline border and a soft focus ring,
// matching Intercom's search-open state rather than the compact grey pill.
export const searchBarInputSx = {
  '.MuiInputBase-root': {
    backgroundColor: (theme) => theme.palette.background.paper,
    border: (theme) => `1px solid ${hairline(theme)}`,
    borderRadius: '10px',
    padding: '7px 16px',
    fontSize: '15px',
    transition: 'border-color 0.12s ease, box-shadow 0.12s ease',
    fieldset: { border: 'none' },
    '&.Mui-focused': {
      borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.28)'),
      boxShadow: (theme) =>
        theme.palette.mode === 'dark' ? '0 0 0 3px rgba(255,255,255,0.06)' : '0 0 0 3px rgba(16,24,40,0.06)',
    },
  },
  'input::placeholder': { opacity: 1, color: (theme) => theme.palette.text.inactive },
};

// The outer flush-page scaffold: cards laid out in a row (stacking on
// mobile) on the shared gray background, with the page's own padding.
//
// Desktop pins the page to the viewport and hands scrolling to whatever is inside
// the cards. Mobile does the opposite and lets the DOCUMENT scroll: the layout's
// mobile top bar already took its slice of the viewport, so a `100vh` page under it
// would always overflow by the bar's height, and stacked cards each competing for
// an internal scroller on a 375px screen is worse than one page scroll. `flex: 1`
// still fills a short page, because the layout's content box is the flex parent.
export const flushPageSx = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: '8px',
  p: { xs: '8px', md: '12px' },
  height: { md: '100vh' },
  overflow: { md: 'hidden' },
};
